/**
 * TKTS Dashboard Component
 *
 * PHASE DETERMINATION (priority order):
 * ======================================
 * 1. booths_open     — at least one booth is currently is_open
 * 2. booths_closed   — both booths are done for the day (past closing or closed today)
 *                      [guard: prevents stale deals_populate after booths close]
 * 3. deals_populate  — fresh scan today + today's showtimes exist, booth not yet open
 * 4. pre_opening     — within 60 min before first booth opens, no deals yet
 * 5. booths_closed   — default (early morning, overnight, no schedule today)
 *
 * BOOTHS CLOSED / PRE-OPENING (same content):
 * ============================================
 * a. Potential Deals for Tomorrow (if any) — after midnight: "Potential Deals for Today"
 * b. High Demand Today/Yesterday (sold out 1+ hr before showtime, if any)
 * c. Today's / Yesterday's Deals (all expired)
 * Booth status: red/closed chips (booths_closed) or grey (pre_opening)
 *
 * DEALS POPULATE (deals live on website, booth not yet open):
 * ===========================================================
 * a. Today's Live Deals (with "Deals are live!" indicator)
 * b. Tomorrow's Deals preview (if any)
 * Booth status: yellow "Deals Live"
 *
 * BOOTHS OPEN:
 * ============
 * a. Hot Deals (deals that disappeared 1+ hr before showtime, if any)
 * b. Today's Live Deals
 * c. Tomorrow's Deals preview (if any)
 * d. Booth Closed Deals (deals from a booth that closed mid-day, if any)
 * e. Expired Deals (showtime passed or within 1 hour)
 * Booth status: green "Open Now"
 *
 * NUANCES:
 * - If only one booth operates today, the flow is the same
 * - If one booth closes mid-day: its deals move to Expired, red chip shown; only enter
 *   booths_closed when BOTH booths are done
 * - After midnight: "today" language shifts to "yesterday"
 *
 * SORTING (applies to all sections):
 * - Primary: Category (Broadway first, Off-Broadway second)
 * - Secondary: Show name (alphabetical)
 * - Tertiary: Showtime (earliest first)
 *
 * FILTERS: Category and Location filters at the top apply to all sections
 */
import React from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  AccessTime,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { getTktsHours, getLiveDeals, getShowStatusFlags, getShowsList } from '../services/api';
import { format, isAfter, startOfDay, addDays } from 'date-fns';
import { LocationHours, LiveDeal, DealVariation, UnavailableDeal, LateAddition } from '../types';
import { getRarityInfo, RarityDisplay } from '../utils/rarity';
import { getShowStatusBadge } from '../utils/showStatus';
import RarityStars from './RarityStars';
import ShowStatusBadgeChip from './ShowStatusBadgeChip';

const TktsHoursCard: React.FC<{
  location: string;
  data: LocationHours;
  dealsLive?: boolean;
  boothDoneForDay?: boolean;
  pastMidnight?: boolean;
}> = ({ location, data, dealsLive, boothDoneForDay, pastMidnight }) => {
  const [expanded, setExpanded] = React.useState(false);

  const getStatus = () => {
    if (data.is_open) return { label: 'Open Now', color: '#56a85e' };
    if (dealsLive) return { label: 'Deals Live', color: '#c89030' };
    if (boothDoneForDay) return { label: 'Closed', color: '#c05a42' };
    return { label: 'Closed', color: '#9e9e9e' };
  };

  const status = getStatus();

  // Find today's index and derive tomorrow's entry
  const todayIdx = data.hours.findIndex((h) => h.is_today);
  const todayHours = todayIdx >= 0 ? data.hours[todayIdx] : null;
  const tomorrowHours = todayIdx >= 0 && todayIdx + 1 < data.hours.length
    ? data.hours[todayIdx + 1]
    : null;

  // After midnight the booths are "done" but haven't opened yet today — show today's hours.
  // During normal closed-for-the-night state, show tomorrow's hours.
  const featured = boothDoneForDay ? (pastMidnight ? todayHours : tomorrowHours) : todayHours;
  const featuredLabel = boothDoneForDay && !pastMidnight ? 'Tomorrow' : 'Today';
  const featuredText = featured
    ? (featured.closed ? 'Closed' : `${featured.open} – ${featured.close}`)
    : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: status.color, flexShrink: 0 }} />
        <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 110 }}>{location}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72 }}>{status.label}</Typography>
        {featuredText && (
          <Typography variant="body2" color="text.secondary">
            {featuredLabel}:{' '}
            <Typography component="span" variant="body2" color="text.primary">{featuredText}</Typography>
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{ ml: 'auto', p: 0.25 }}
          aria-label={expanded ? 'collapse hours' : 'expand hours'}
        >
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ pl: 2.5, mt: 0.5 }}>
          {data.hours.map((day) => (
            <Box
              key={day.day}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 0.25,
                px: 0.5,
                borderRadius: 0.5,
                bgcolor: day.is_today ? 'rgba(61, 122, 69, 0.15)' : 'transparent',
              }}
            >
              <Typography variant="caption">{day.day}</Typography>
              <Typography variant="caption">{day.closed ? 'Closed' : `${day.open} – ${day.close}`}</Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// Sorting helper: Category (Broadway first), then show name, then earliest showtime
const sortDeals = (deals: LiveDeal[]): LiveDeal[] => {
  return [...deals].sort((a, b) => {
    // 1. Category: Broadway first, Off-Broadway second
    const categoryOrder = (cat: string) => (cat === 'Broadway' ? 0 : 1);
    const catCompare = categoryOrder(a.category) - categoryOrder(b.category);
    if (catCompare !== 0) return catCompare;

    // 2. Show name (alphabetical)
    const nameCompare = a.show_name.localeCompare(b.show_name);
    if (nameCompare !== 0) return nameCompare;

    // 3. Earliest showtime
    const aEarliest = a.all_showtimes.length > 0 ? new Date(a.all_showtimes[0]).getTime() : 0;
    const bEarliest = b.all_showtimes.length > 0 ? new Date(b.all_showtimes[0]).getTime() : 0;
    return aEarliest - bEarliest;
  });
};

// Sorting helper for unavailable deals (sold out / expired)
const sortUnavailableDeals = (deals: UnavailableDeal[]): UnavailableDeal[] => {
  return [...deals].sort((a, b) => {
    // 1. Category: Broadway first, Off-Broadway second
    const categoryOrder = (cat: string) => (cat === 'Broadway' ? 0 : 1);
    const catCompare = categoryOrder(a.category) - categoryOrder(b.category);
    if (catCompare !== 0) return catCompare;

    // 2. Show name (alphabetical)
    const nameCompare = a.show_name.localeCompare(b.show_name);
    if (nameCompare !== 0) return nameCompare;

    // 3. Showtime
    const aTime = a.show_datetime ? new Date(a.show_datetime).getTime() : 0;
    const bTime = b.show_datetime ? new Date(b.show_datetime).getTime() : 0;
    return aTime - bTime;
  });
};

// Group unavailable deals by show name (similar to LiveDeal structure)
interface GroupedUnavailableDeal {
  show_name: string;
  category: string;
  showtimes: string[]; // ISO datetime strings
  discount: number;
  priceRange: string;
  locations: string[];
  lastSeenByLocation: Record<string, string>; // location -> last seen time string
}

const groupUnavailableDeals = (deals: UnavailableDeal[]): GroupedUnavailableDeal[] => {
  const grouped = new Map<string, GroupedUnavailableDeal>();

  deals.forEach((deal) => {
    if (!grouped.has(deal.show_name)) {
      grouped.set(deal.show_name, {
        show_name: deal.show_name,
        category: deal.category,
        showtimes: [],
        discount: deal.last_seen_discount,
        priceRange: deal.last_seen_price_range,
        locations: [],
        lastSeenByLocation: {},
      });
    }

    const group = grouped.get(deal.show_name)!;
    if (deal.show_datetime && !group.showtimes.includes(deal.show_datetime)) {
      group.showtimes.push(deal.show_datetime);
    }
    if (!group.locations.includes(deal.location)) {
      group.locations.push(deal.location);
    }
    // Track last seen time by location
    group.lastSeenByLocation[deal.location] = deal.last_seen_time;
  });

  // Sort showtimes within each group and sort locations (TS first)
  const result = Array.from(grouped.values()).map((g) => ({
    ...g,
    showtimes: g.showtimes.sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
    locations: g.locations.sort((a, b) => {
      if (a === 'Times Square') return -1;
      if (b === 'Times Square') return 1;
      return a.localeCompare(b);
    }),
  }));

  // Sort by category (Broadway first), then show name
  return result.sort((a, b) => {
    const categoryOrder = (cat: string) => (cat === 'Broadway' ? 0 : 1);
    const catCompare = categoryOrder(a.category) - categoryOrder(b.category);
    if (catCompare !== 0) return catCompare;
    return a.show_name.localeCompare(b.show_name);
  });
};

// Convert booth-closed LiveDeals to GroupedUnavailableDeal format so they can be merged into Expired Deals
const convertBoothClosedToGrouped = (deals: LiveDeal[]): GroupedUnavailableDeal[] => {
  const today = startOfDay(new Date());
  const tomorrow = startOfDay(addDays(new Date(), 1));
  return deals.map((deal) => {
    const todayVars = filterTodayShowtimes(deal.variations);
    const allPrices = todayVars.flatMap((v) => [v.min_price, v.max_price]);
    const minPrice = allPrices.length > 0 ? Math.min(...allPrices) : 0;
    const maxPrice = allPrices.length > 0 ? Math.max(...allPrices) : 0;
    const priceRange = allPrices.length > 0
      ? (minPrice === maxPrice ? `$${minPrice.toFixed(0)}` : `$${minPrice.toFixed(0)}-$${maxPrice.toFixed(0)}`)
      : '';
    const showtimes = deal.all_showtimes
      .filter((st) => { const dt = new Date(st); return isAfter(dt, today) && !isAfter(dt, tomorrow); })
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const locationSet = new Set(todayVars.flatMap((v) => v.locations));
    const locations = Array.from(locationSet).sort((a, b) => {
      if (a === 'Times Square') return -1;
      if (b === 'Times Square') return 1;
      return a.localeCompare(b);
    });
    return { show_name: deal.show_name, category: deal.category, showtimes, discount: deal.best_discount, priceRange, locations, lastSeenByLocation: {} };
  });
};

// Helper to check if any showtime is for tomorrow
const hasTomorrowShowtime = (showtimes: string[]): boolean => {
  const tomorrow = startOfDay(addDays(new Date(), 1));
  const dayAfterTomorrow = startOfDay(addDays(new Date(), 2));
  return showtimes.some((st) => {
    const showDate = new Date(st);
    return isAfter(showDate, tomorrow) && !isAfter(showDate, dayAfterTomorrow);
  });
};

// Helper to check if any showtime is for today
const hasTodayShowtime = (showtimes: string[]): boolean => {
  const today = startOfDay(new Date());
  const tomorrow = startOfDay(addDays(new Date(), 1));
  return showtimes.some((st) => {
    const showDate = new Date(st);
    return isAfter(showDate, today) && !isAfter(showDate, tomorrow);
  });
};

// Helper to check if any showtime is in the future (used when booths are closed)
const hasFutureShowtime = (showtimes: string[]): boolean => {
  const now = new Date();
  return showtimes.some((st) => isAfter(new Date(st), now));
};

// Display phase type
// booths_closed: both done for the day (or default overnight)
// pre_opening: within 1 hr of opening, deals not yet populated
// deals_populate: deals have populated on website, booth not yet open
// booths_open: at least one booth is currently is_open
type DisplayPhase = 'booths_closed' | 'pre_opening' | 'deals_populate' | 'booths_open';

// Helper to determine if we're past midnight (deals from "today" become "yesterday")
// Returns true if current time is before the earliest booth opening today
const isPastMidnight = (tktsHours: Record<string, LocationHours> | undefined): boolean => {
  if (!tktsHours) return false;

  const now = new Date();
  const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

  // Find the earliest booth opening time today
  let earliestOpeningInMinutes = Infinity;
  for (const location of Object.values(tktsHours)) {
    const todayHours = location.hours.find((h) => h.is_today);
    if (todayHours && !todayHours.closed && todayHours.open) {
      const openingInMinutes = parseTimeToMinutes(todayHours.open);
      earliestOpeningInMinutes = Math.min(earliestOpeningInMinutes, openingInMinutes);
    }
  }

  // If we know when booths open, we're "past midnight" (yesterday's data) until the first booth opens
  if (earliestOpeningInMinutes !== Infinity) {
    return currentTimeInMinutes < earliestOpeningInMinutes;
  }

  // Fallback for days when no booths operate
  return now.getHours() < 10;
};

// Helper to check if the last scan was from today
// This is critical to prevent showing yesterday's deals as "live"
const wasScannedToday = (lastScanTime: string | undefined): boolean => {
  if (!lastScanTime) return false;

  const scanDate = new Date(lastScanTime);
  const today = startOfDay(new Date());

  return isAfter(scanDate, today);
};

// Helper to parse time string like "3:00 PM" to minutes since midnight
const parseTimeToMinutes = (timeStr: string): number => {
  const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return 0;
  let hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  const period = match[3].toUpperCase();
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  return hours * 60 + minutes;
};

// Helper to check if we're within 60 minutes before the earliest booth opening today
// This is the pre-opening window when the scheduler scans every minute for deals
const isWithinHourOfOpening = (tktsHours: Record<string, LocationHours> | undefined): boolean => {
  if (!tktsHours) return false;

  const now = new Date();
  const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

  let earliestOpeningInMinutes = Infinity;

  for (const location of Object.values(tktsHours)) {
    const todayHours = location.hours.find((h) => h.is_today);
    if (todayHours && !todayHours.closed && todayHours.open) {
      const openingInMinutes = parseTimeToMinutes(todayHours.open);
      earliestOpeningInMinutes = Math.min(earliestOpeningInMinutes, openingInMinutes);
    }
  }

  if (earliestOpeningInMinutes === Infinity) return false;

  // Within 60 minutes before opening, and not yet past opening time
  const windowStart = earliestOpeningInMinutes - 60;
  return currentTimeInMinutes >= windowStart && currentTimeInMinutes < earliestOpeningInMinutes;
};

// Helper to check if both booths are done for the day (past closing or don't operate today)
// This guard prevents deals_populate from firing after booths have already closed
const bothBoothsDoneForDay = (tktsHours: Record<string, LocationHours> | undefined): boolean => {
  if (!tktsHours) return false;
  const states = Object.values(tktsHours).map((loc) => getBoothState(loc));
  return states.every((s) => s === 'closed' || s === 'closed_today');
};

// Booth state: 'pre_opening' (not open yet today), 'open', 'closed' (done for day), 'closed_today' (doesn't operate today)
type BoothState = 'pre_opening' | 'open' | 'closed' | 'closed_today';

// Active booths interface with state information
interface ActiveBooths {
  timesSquare: boolean;
  lincolnCenter: boolean;
  timesSquareState: BoothState;
  lincolnCenterState: BoothState;
}

// Helper to determine the state of a single booth
const getBoothState = (locationHours: LocationHours | undefined): BoothState => {
  if (!locationHours) return 'closed_today';

  // If booth is currently open, return 'open'
  if (locationHours.is_open) return 'open';

  // Find today's hours
  const todayHours = locationHours.hours.find((h) => h.is_today);

  // If closed today (doesn't operate), return 'closed_today'
  if (!todayHours || todayHours.closed || !todayHours.open) return 'closed_today';

  // Parse opening and closing times
  const now = new Date();
  const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

  const openingMinutes = parseTimeToMinutes(todayHours.open);
  const closingMinutes = todayHours.close ? parseTimeToMinutes(todayHours.close) : 24 * 60;

  // If current time is before opening, booth is pre-opening
  if (currentTimeInMinutes < openingMinutes) return 'pre_opening';

  // If current time is after closing, booth is closed
  if (currentTimeInMinutes > closingMinutes) return 'closed';

  // Shouldn't reach here if is_open is accurate, but default to closed
  return 'closed';
};

// Helper to get which booths are currently active (open) and their states
const getActiveBooths = (tktsHours: Record<string, LocationHours> | undefined): ActiveBooths => {
  if (!tktsHours) {
    return {
      timesSquare: false,
      lincolnCenter: false,
      timesSquareState: 'closed_today',
      lincolnCenterState: 'closed_today',
    };
  }

  const timesSquareState = getBoothState(tktsHours['Times Square']);
  const lincolnCenterState = getBoothState(tktsHours['Lincoln Center']);

  return {
    timesSquare: tktsHours['Times Square']?.is_open ?? false,
    lincolnCenter: tktsHours['Lincoln Center']?.is_open ?? false,
    timesSquareState,
    lincolnCenterState,
  };
};

// Helper to check if a booth should show deals (open or pre-opening, not closed)
const shouldShowDealsForBooth = (state: BoothState): boolean => {
  // Show deals for booths that are open OR haven't opened yet today (pre-opening)
  // Don't show deals for booths that have already closed or don't operate today
  return state === 'open' || state === 'pre_opening';
};

// Helper to filter variations to only include locations where deals should be shown
// Used when one booth has closed for the day but another is still open/pre-opening
const filterVariationsForActiveBooths = (
  variations: DealVariation[],
  activeBooths: ActiveBooths
): DealVariation[] => {
  const timesSquareShowDeals = shouldShowDealsForBooth(activeBooths.timesSquareState);
  const lincolnCenterShowDeals = shouldShowDealsForBooth(activeBooths.lincolnCenterState);

  const bothShowDeals = timesSquareShowDeals && lincolnCenterShowDeals;
  const neitherShowDeals = !timesSquareShowDeals && !lincolnCenterShowDeals;

  // No filtering needed if both are in the same state (both showing or both not showing)
  if (bothShowDeals || neitherShowDeals) {
    return variations;
  }

  // Partial state: filter variations to only include locations that should show deals
  // This happens when one booth has closed but the other is still open or pre-opening
  return variations
    .map((v) => ({
      ...v,
      locations: v.locations.filter((loc) => {
        if (loc === 'Times Square') return timesSquareShowDeals;
        if (loc === 'Lincoln Center') return lincolnCenterShowDeals;
        return true;
      }),
    }))
    .filter((v) => v.locations.length > 0);
};

// Helper to filter today's showtimes from variations
const filterTodayShowtimes = (variations: DealVariation[]): DealVariation[] => {
  const today = startOfDay(new Date());
  const tomorrow = startOfDay(addDays(new Date(), 1));

  return variations
    .map((v) => ({
      ...v,
      showtimes: v.showtimes.filter((st) => {
        const showDate = new Date(st);
        return isAfter(showDate, today) && !isAfter(showDate, tomorrow);
      }),
    }))
    .filter((v) => v.showtimes.length > 0);
};

// Helper to filter tomorrow's showtimes from variations
const filterTomorrowShowtimes = (variations: DealVariation[]): DealVariation[] => {
  const tomorrow = startOfDay(addDays(new Date(), 1));
  const dayAfterTomorrow = startOfDay(addDays(new Date(), 2));

  return variations
    .map((v) => ({
      ...v,
      showtimes: v.showtimes.filter((st) => {
        const showDate = new Date(st);
        return isAfter(showDate, tomorrow) && !isAfter(showDate, dayAfterTomorrow);
      }),
    }))
    .filter((v) => v.showtimes.length > 0);
};

// Structure for a single showtime's data
interface ShowtimeData {
  showtime: string;
  formattedTime: string;
  // Entries by location - if same at both, one entry with both locations
  // If different, separate entries sorted with TS first
  entries: Array<{
    discount: number;
    minPrice: number;
    maxPrice: number;
    locations: string[];
  }>;
}

// Format variations for display - collapses identical values across showtimes
const formatVariations = (variations: DealVariation[]): {
  discountDisplay: React.ReactNode;
  priceDisplay: React.ReactNode;
  showtimesDisplay: React.ReactNode;
  locationsDisplay: React.ReactNode;
} => {
  // Build a map: showtime -> location -> { discount, minPrice, maxPrice }
  const showtimeMap = new Map<string, Map<string, { discount: number; minPrice: number; maxPrice: number }>>();

  variations.forEach((v) => {
    v.showtimes.forEach((st) => {
      if (!showtimeMap.has(st)) {
        showtimeMap.set(st, new Map());
      }
      const locMap = showtimeMap.get(st)!;
      v.locations.forEach((loc) => {
        locMap.set(loc, {
          discount: v.percentage_off,
          minPrice: v.min_price,
          maxPrice: v.max_price,
        });
      });
    });
  });

  // Sort showtimes by time of day
  const sortedShowtimes = Array.from(showtimeMap.keys()).sort((a, b) => {
    const timeA = new Date(a).getHours() * 60 + new Date(a).getMinutes();
    const timeB = new Date(b).getHours() * 60 + new Date(b).getMinutes();
    return timeA - timeB;
  });

  // Helper functions
  const formatPrice = (min: number, max: number) =>
    min === max ? `$${min.toFixed(0)}` : `$${min.toFixed(0)}-$${max.toFixed(0)}`;

  const formatLocAbbrev = (loc: string) => (loc === 'Times Square' ? 'TS' : 'LC');

  // Build ShowtimeData for each showtime
  const showtimeRows: ShowtimeData[] = sortedShowtimes.map((st) => {
    const locMap = showtimeMap.get(st)!;
    const locations = Array.from(locMap.keys());

    // Sort locations: Times Square first
    locations.sort((a, b) => {
      if (a === 'Times Square') return -1;
      if (b === 'Times Square') return 1;
      return a.localeCompare(b);
    });

    // Check if all locations have the same price/discount
    const values = Array.from(locMap.values());
    const allSame = values.length > 0 && values.every(
      (v) =>
        v.discount === values[0].discount &&
        v.minPrice === values[0].minPrice &&
        v.maxPrice === values[0].maxPrice
    );

    let entries: ShowtimeData['entries'];
    if (allSame && locations.length > 0) {
      entries = [{
        discount: values[0].discount,
        minPrice: values[0].minPrice,
        maxPrice: values[0].maxPrice,
        locations: locations,
      }];
    } else {
      entries = locations.map((loc) => {
        const data = locMap.get(loc)!;
        return {
          discount: data.discount,
          minPrice: data.minPrice,
          maxPrice: data.maxPrice,
          locations: [loc],
        };
      });
    }

    return {
      showtime: st,
      formattedTime: format(new Date(st), 'h:mm a'),
      entries,
    };
  });

  // Check if ALL showtimes have identical discount, price, and locations
  // If so, we only display the values once (not per showtime)
  const allRowsIdentical = showtimeRows.length > 1 && showtimeRows.every((row) => {
    const first = showtimeRows[0];
    if (row.entries.length !== first.entries.length) return false;
    return row.entries.every((entry, i) => {
      const firstEntry = first.entries[i];
      return (
        entry.discount === firstEntry.discount &&
        entry.minPrice === firstEntry.minPrice &&
        entry.maxPrice === firstEntry.maxPrice &&
        entry.locations.length === firstEntry.locations.length &&
        entry.locations.every((loc, j) => loc === firstEntry.locations[j])
      );
    });
  });

  // Build the stacked display for showtimes (always show all)
  const showtimeLines: React.ReactNode[] = showtimeRows.map((row, idx) => (
    <Box key={idx} sx={{ mb: idx < showtimeRows.length - 1 ? 0.5 : 0 }}>
      {row.formattedTime}
    </Box>
  ));

  // If all rows are identical, only show discount/price/location once
  if (allRowsIdentical && showtimeRows.length > 0) {
    const firstRow = showtimeRows[0];
    const entry = firstRow.entries[0];

    return {
      showtimesDisplay: <Box>{showtimeLines}</Box>,
      discountDisplay: (
        <Chip
          label={`${entry.discount}%`}
          color={entry.discount >= 50 ? 'success' : 'warning'}
          size="small"
        />
      ),
      priceDisplay: formatPrice(entry.minPrice, entry.maxPrice),
      locationsDisplay: (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {entry.locations.map((loc) => (
            <Chip
              key={loc}
              label={formatLocAbbrev(loc)}
              size="small"
              variant="outlined"
              color={loc === 'Times Square' ? 'primary' : 'secondary'}
            />
          ))}
        </Box>
      ),
    };
  }

  // Otherwise, show values per showtime row
  const discountLines: React.ReactNode[] = [];
  const priceLines: React.ReactNode[] = [];
  const locationLines: React.ReactNode[] = [];

  showtimeRows.forEach((row, idx) => {
    if (row.entries.length === 1) {
      const entry = row.entries[0];
      discountLines.push(
        <Box key={idx} sx={{ mb: idx < showtimeRows.length - 1 ? 0.5 : 0 }}>
          <Chip
            label={`${entry.discount}%`}
            color={entry.discount >= 50 ? 'success' : 'warning'}
            size="small"
          />
        </Box>
      );
      priceLines.push(
        <Box key={idx} sx={{ mb: idx < showtimeRows.length - 1 ? 0.5 : 0 }}>
          {formatPrice(entry.minPrice, entry.maxPrice)}
        </Box>
      );
      locationLines.push(
        <Box key={idx} sx={{ display: 'flex', gap: 0.5, mb: idx < showtimeRows.length - 1 ? 0.5 : 0 }}>
          {entry.locations.map((loc) => (
            <Chip
              key={loc}
              label={formatLocAbbrev(loc)}
              size="small"
              variant="outlined"
              color={loc === 'Times Square' ? 'primary' : 'secondary'}
            />
          ))}
        </Box>
      );
    } else {
      // Different by location - check if discounts are the same even if prices differ
      const allDiscountsSame = row.entries.every((e) => e.discount === row.entries[0].discount);

      if (allDiscountsSame) {
        // Discounts are the same, show single discount without location labels
        discountLines.push(
          <Box key={idx} sx={{ mb: idx < showtimeRows.length - 1 ? 0.5 : 0 }}>
            <Chip
              label={`${row.entries[0].discount}%`}
              color={row.entries[0].discount >= 50 ? 'success' : 'warning'}
              size="small"
            />
          </Box>
        );
      } else {
        // Discounts differ - comma separate with location labels
        discountLines.push(
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: idx < showtimeRows.length - 1 ? 0.5 : 0 }}>
            {row.entries.map((e, i) => (
              <React.Fragment key={i}>
                {i > 0 && <Typography component="span" variant="body2">,</Typography>}
                <Chip
                  label={`${e.discount}%`}
                  color={e.discount >= 50 ? 'success' : 'warning'}
                  size="small"
                />
                <Typography variant="caption" color="text.secondary">
                  ({e.locations.map(formatLocAbbrev).join('/')})
                </Typography>
              </React.Fragment>
            ))}
          </Box>
        );
      }
      priceLines.push(
        <Box key={idx} sx={{ mb: idx < showtimeRows.length - 1 ? 0.5 : 0 }}>
          {row.entries.map((e, i) => (
            <React.Fragment key={i}>
              {i > 0 && ', '}
              {formatPrice(e.minPrice, e.maxPrice)} ({e.locations.map(formatLocAbbrev).join('/')})
            </React.Fragment>
          ))}
        </Box>
      );
      locationLines.push(
        <Box key={idx} sx={{ display: 'flex', gap: 0.5, mb: idx < showtimeRows.length - 1 ? 0.5 : 0 }}>
          {row.entries.flatMap((e) => e.locations).map((loc) => (
            <Chip
              key={loc}
              label={formatLocAbbrev(loc)}
              size="small"
              variant="outlined"
              color={loc === 'Times Square' ? 'primary' : 'secondary'}
            />
          ))}
        </Box>
      );
    }
  });

  return {
    showtimesDisplay: <Box>{showtimeLines}</Box>,
    discountDisplay: <Box>{discountLines}</Box>,
    priceDisplay: <Box>{priceLines}</Box>,
    locationsDisplay: <Box>{locationLines}</Box>,
  };
};

// Compute a show-status badge based on IBDB date metadata and the showtime date
// Returns the date portion (midnight) of the earliest showtime in the list
const getDealDate = (showtimes: string[]): Date => {
  const d = showtimes.length > 0 ? new Date(showtimes[0]) : new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// Asterisk marker for shows that showed up at a booth 1+ hour after it
// opened — often a fresh batch of tickets released mid-day.
const LateAddFlag: React.FC<{ lateAdditions?: LateAddition[] }> = ({ lateAdditions }) => {
  if (!lateAdditions || lateAdditions.length === 0) return null;
  const detail = lateAdditions
    .map((l) => `${l.location} at ${l.first_seen_time}`)
    .join(' and ');
  return (
    <Tooltip
      title={`Added late — this show wasn't listed when the booth opened. It showed up at ${detail}, so a fresh batch of tickets may have just been released.`}
      arrow
      placement="top"
    >
      <Box
        component="span"
        sx={{ color: '#4fc3f7', fontWeight: 700, fontSize: '1rem', lineHeight: 1, cursor: 'help' }}
      >
        {'✱'}
      </Box>
    </Tooltip>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const openShow = (name: string, e: React.MouseEvent) => {
    const url = `/shows/${encodeURIComponent(name)}`;
    if (e.ctrlKey || e.metaKey) window.open(url, '_blank');
    else navigate(url);
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const [categoryFilter, setCategoryFilter] = React.useState<string>('all');
  const [locationFilter, setLocationFilter] = React.useState<string>('');

  const {
    data: liveDeals,
    isLoading: dealsLoading,
    error: dealsError,
  } = useQuery({
    queryKey: ['liveDeals', locationFilter, categoryFilter === 'all' ? '' : categoryFilter],
    queryFn: () => getLiveDeals(
      locationFilter || undefined,
      categoryFilter === 'all' ? undefined : categoryFilter
    ),
    refetchInterval: 60000,
    placeholderData: keepPreviousData,
  });

  const {
    data: tktsHours,
    isLoading: hoursLoading,
  } = useQuery({
    queryKey: ['tktsHours'],
    queryFn: getTktsHours,
    refetchInterval: 60000,
  });

  const { data: statusFlags } = useQuery({
    queryKey: ['showStatusFlags'],
    queryFn: getShowStatusFlags,
    refetchInterval: 300000, // refresh every 5 min; dates don't change often
    staleTime: 300000,
  });

  const { data: showsList } = useQuery({
    queryKey: ['shows'],
    queryFn: getShowsList,
    staleTime: 300000,
  });

  // Build rarity lookup: show_name → display info (stars, tooltip, trend)
  const rarityByShow = React.useMemo(() => {
    const map = new Map<string, RarityDisplay>();
    if (showsList) {
      showsList.forEach((s) => {
        // Prefer has_tkts_data: true entries — don't let a master-only entry overwrite real TKTS data
        if (!map.has(s.show_name) || s.has_tkts_data !== false) {
          map.set(s.show_name, getRarityInfo(s));
        }
      });
    }
    return map;
  }, [showsList]);

  if (dealsLoading || hoursLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (dealsError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load dashboard data. Make sure the API is running at
        http://localhost:8001
      </Alert>
    );
  }

  // Check if any booth is currently open
  const anyBoothOpen = tktsHours
    ? Object.values(tktsHours).some((loc) => loc.is_open)
    : false;

  // Get active booths status for partial-close filtering
  const activeBooths = getActiveBooths(tktsHours);

  // Get deals from API response and apply sorting
  const currentDeals = sortDeals(liveDeals?.current_deals || []);
  const soldOutDeals = sortUnavailableDeals(liveDeals?.sold_out_deals || []);
  const expiredDeals = sortUnavailableDeals(liveDeals?.expired_deals || []);
  const noShowDeals = sortUnavailableDeals(liveDeals?.no_show_deals || []);
  const lastScanTime = liveDeals?.last_scan_time;

  const hasLiveTodayDeals = currentDeals.some((deal) => hasTodayShowtime(deal.all_showtimes));
  const hasFreshScansToday = wasScannedToday(lastScanTime);
  const boothsDone = bothBoothsDoneForDay(tktsHours);
  const withinHourOfOpening = isWithinHourOfOpening(tktsHours);

  // Determine display phase (priority order):
  // 1. booths_open  - at least one booth is currently is_open
  // 2. booths_closed - both booths done for the day (guard against stale deals_populate)
  // 3. deals_populate - fresh scan today + today's showtimes exist, booth not open yet
  // 4. pre_opening  - within 60 min of opening, no deals populated yet
  // 5. booths_closed - default (early morning, overnight, or no schedule today)
  let displayPhase: DisplayPhase = 'booths_closed';

  if (anyBoothOpen) {
    displayPhase = 'booths_open';
  } else if (boothsDone) {
    displayPhase = 'booths_closed';
  } else if (hasFreshScansToday && hasLiveTodayDeals) {
    displayPhase = 'deals_populate';
  } else if (withinHourOfOpening) {
    displayPhase = 'pre_opening';
  }

  // Check if we're past midnight (for Today/Yesterday language)
  const pastMidnight = isPastMidnight(tktsHours);

  // Split into today's and tomorrow's shows (unfiltered) - already sorted via currentDeals
  const allTodayDeals = currentDeals.filter((deal) => hasTodayShowtime(deal.all_showtimes));
  const tomorrowDeals = currentDeals.filter((deal) => hasTomorrowShowtime(deal.all_showtimes));
  // Note: filtering preserves sort order since currentDeals is already sorted

  // In booths_closed/pre_opening after midnight, yesterday's "tomorrow" deals are now "today" deals
  // So we show allTodayDeals as "Potential Deals for Today" instead of tomorrowDeals
  const isClosedPhase = displayPhase === 'booths_closed' || displayPhase === 'pre_opening';
  const potentialDeals = isClosedPhase && pastMidnight ? allTodayDeals : tomorrowDeals;

  // When booth is open, only show expired deals from today — not stale data from yesterday
  // (backend may fall back to yesterday's reference date if today's scans haven't started yet)
  const todayOnlyExpiredDeals = displayPhase === 'booths_open'
    ? expiredDeals.filter((deal) => {
        if (!deal.show_datetime) return false;
        const dt = new Date(deal.show_datetime);
        const todayStart = startOfDay(new Date());
        const todayEnd = startOfDay(addDays(new Date(), 1));
        return isAfter(dt, todayStart) && !isAfter(dt, todayEnd);
      })
    : expiredDeals;

  // Filter today's deals based on booth status (for partial close scenario)
  // Deals with no remaining variations after filtering will be moved to expired
  const todayDeals: LiveDeal[] = [];
  const boothClosedDeals: LiveDeal[] = []; // Deals that were only at closed booth

  allTodayDeals.forEach((deal) => {
    // Filter variations to only include today's showtimes from active booths
    const todayVariations = filterTodayShowtimes(deal.variations);
    const filteredVariations = filterVariationsForActiveBooths(todayVariations, activeBooths);

    if (filteredVariations.length > 0) {
      // Deal still has variations at open booths
      todayDeals.push({
        ...deal,
        variations: filteredVariations,
        all_locations: Array.from(new Set(filteredVariations.flatMap((v) => v.locations))),
      });
    } else if (todayVariations.length > 0) {
      // Deal had today variations but all were at closed booths
      boothClosedDeals.push(deal);
    }
  });

  // When booths are closed and no today/tomorrow deals, show all future deals as "upcoming"
  // This handles edge cases like after midnight when yesterday's "tomorrow" becomes "today"
  const futureDeals = currentDeals.filter((deal) => hasFutureShowtime(deal.all_showtimes));
  const showUpcomingSection = isClosedPhase && todayDeals.length === 0 && tomorrowDeals.length === 0 && futureDeals.length > 0;

  // Pre-compute Hot Deals card so it can be placed first in booths_open or after Potential in closed phases
  const hotDealsCard = (() => {
    if (displayPhase === 'deals_populate' || soldOutDeals.length === 0) return null;

    const activeLocationsByShow = new Map<string, Set<string>>();
    todayDeals.forEach((deal) => {
      activeLocationsByShow.set(deal.show_name, new Set(deal.all_locations));
    });

    const groupedSoldOut = groupUnavailableDeals(soldOutDeals)
      .map((deal) => {
        const activeLocations = activeLocationsByShow.get(deal.show_name);
        if (!activeLocations) return deal;
        const soldOutLocations = deal.locations.filter((loc) => !activeLocations.has(loc));
        const filteredLastSeen: Record<string, string> = {};
        soldOutLocations.forEach((loc) => {
          if (deal.lastSeenByLocation[loc]) filteredLastSeen[loc] = deal.lastSeenByLocation[loc];
        });
        return { ...deal, locations: soldOutLocations, lastSeenByLocation: filteredLastSeen };
      })
      .filter((deal) => deal.locations.length > 0);

    if (groupedSoldOut.length === 0) return null;

    const formatLastSeen = (lastSeenByLocation: Record<string, string>, locations: string[]) => {
      const times = locations.map((loc) => lastSeenByLocation[loc]).filter(Boolean);
      const uniqueTimes = Array.from(new Set(times));
      if (uniqueTimes.length === 0) return '-';
      if (uniqueTimes.length === 1) return uniqueTimes[0];
      return locations
        .filter((loc) => lastSeenByLocation[loc])
        .map((loc) => `${lastSeenByLocation[loc]} (${loc === 'Times Square' ? 'TS' : 'LC'})`)
        .join(', ');
    };

    return (
      <Card sx={{ mb: 3, border: '1px solid', borderColor: 'error.light' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6">
              {isClosedPhase
                ? `High Demand ${pastMidnight ? 'Yesterday' : 'Today'} (${groupedSoldOut.length} shows)`
                : `Hot Deals (${groupedSoldOut.length} shows)`
              }
            </Typography>
            <Chip label="High demand" color="error" size="small" variant="outlined" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isClosedPhase
              ? `These high-demand shows sold out ${pastMidnight ? 'yesterday' : 'today'} before their showtime`
              : 'These deals disappeared more than 1 hour before showtime — likely sold out'
            }
          </Typography>
          {isMobile ? (
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              {groupedSoldOut.map((deal, idx) => {
                const prevDeal = idx > 0 ? groupedSoldOut[idx - 1] : null;
                const isCategoryBoundary = prevDeal && prevDeal.category === 'Broadway' && deal.category === 'Off-Broadway';
                const dealDate = getDealDate(deal.showtimes);
                return (
                  <Box
                    key={`soldout-${deal.show_name}`}
                    onClick={(e) => openShow(deal.show_name, e)}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      borderTop: isCategoryBoundary ? '3px solid' : '1px solid',
                      borderTopColor: isCategoryBoundary ? 'divider' : 'rgba(255,255,255,0.08)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.25 }}>
                      <Typography fontWeight="medium" variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>{deal.show_name}</Typography>
                      <ShowStatusBadgeChip badge={getShowStatusBadge(deal.show_name, statusFlags, dealDate)} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">{deal.category}</Typography>
                      {rarityByShow.has(deal.show_name) && <RarityStars size="sm" {...rarityByShow.get(deal.show_name)!} />}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box sx={{ minWidth: 60 }}>
                        <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Time</Typography>
                        {deal.showtimes.map((st, i) => <Box key={i}>{format(new Date(st), 'h:mm a')}</Box>)}
                      </Box>
                      <Box sx={{ minWidth: 44 }}>
                        <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Disc.</Typography>
                        <Chip label={`${deal.discount}%`} color="default" size="small" sx={{ opacity: 0.5 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Price</Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ textDecoration: 'line-through' }}>{deal.priceRange}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Seen: {formatLastSeen(deal.lastSeenByLocation, deal.locations)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>At</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {deal.locations.map((loc) => (
                            <Chip key={loc} label={loc === 'Times Square' ? 'TS' : 'LC'} size="small" variant="outlined"
                              color={loc === 'Times Square' ? 'primary' : 'secondary'} />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Paper>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Show</TableCell>
                    <TableCell>Show Time</TableCell>
                    <TableCell>Discount</TableCell>
                    <TableCell>Price Range</TableCell>
                    <TableCell>Last Seen</TableCell>
                    <TableCell>Location</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedSoldOut.map((deal, idx) => {
                    const prevDeal = idx > 0 ? groupedSoldOut[idx - 1] : null;
                    const isCategoryBoundary = prevDeal && prevDeal.category === 'Broadway' && deal.category === 'Off-Broadway';
                    const dealDate = getDealDate(deal.showtimes);
                    return (
                      <TableRow
                        key={`soldout-${deal.show_name}`}
                        hover
                        sx={{
                          cursor: 'pointer',
                          verticalAlign: 'top',
                          ...(isCategoryBoundary && { borderTop: '3px solid', borderTopColor: 'divider' }),
                        }}
                        onClick={(e) => openShow(deal.show_name, e)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Typography fontWeight="medium" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>{deal.show_name}</Typography>
                            <ShowStatusBadgeChip badge={getShowStatusBadge(deal.show_name, statusFlags, dealDate)} />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">{deal.category}</Typography>
                            {rarityByShow.has(deal.show_name) && <RarityStars size="sm" {...rarityByShow.get(deal.show_name)!} />}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {deal.showtimes.map((st, stIdx) => (
                            <Box key={stIdx} sx={{ mb: stIdx < deal.showtimes.length - 1 ? 0.5 : 0 }}>
                              {format(new Date(st), 'h:mm a')}
                            </Box>
                          ))}
                        </TableCell>
                        <TableCell>
                          <Chip label={`${deal.discount}%`} color="default" size="small" sx={{ opacity: 0.5 }} />
                        </TableCell>
                        <TableCell><Typography color="text.disabled" sx={{ textDecoration: 'line-through' }}>{deal.priceRange}</Typography></TableCell>
                        <TableCell>{formatLastSeen(deal.lastSeenByLocation, deal.locations)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {deal.locations.map((loc) => (
                              <Chip key={loc} label={loc === 'Times Square' ? 'TS' : 'LC'} size="small" variant="outlined"
                                color={loc === 'Times Square' ? 'primary' : 'secondary'} />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    );
  })();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="h4" fontWeight="bold">
          TKTS Dashboard
        </Typography>
        <Typography
          component="a"
          href="https://www.tdf.org/discount-ticket-programs/tkts-by-tdf/tkts-live/#tkts-tab-times-square"
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{ color: 'primary.light', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          TKTS Live ↗
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Real-time Broadway discount ticket tracking
        {lastScanTime && (
          <Typography variant="caption" sx={{ ml: 2 }}>
            Last scan: {format(new Date(lastScanTime), 'h:mm a')}
          </Typography>
        )}
      </Typography>

      {/* TKTS Booth Hours */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AccessTime fontSize="small" color="action" />
            <Typography variant="subtitle2" color="text.secondary">TKTS Booth Hours</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {tktsHours &&
              Object.entries(tktsHours).map(([location, data]) => {
                const locationHasDeals = currentDeals.some(
                  (deal) => deal.all_locations.includes(location) && hasTodayShowtime(deal.all_showtimes)
                );
                const dealsLiveAtLocation = !data.is_open && displayPhase === 'deals_populate' && locationHasDeals;
                const boothDoneForDay = displayPhase === 'booths_closed' && !data.is_open && !dealsLiveAtLocation;

                return (
                  <TktsHoursCard
                    key={location}
                    location={location}
                    data={data}
                    dealsLive={dealsLiveAtLocation}
                    boothDoneForDay={boothDoneForDay}
                    pastMidnight={pastMidnight}
                  />
                );
              })}
          </Box>
        </CardContent>
      </Card>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <ToggleButtonGroup
          value={categoryFilter}
          exclusive
          onChange={(_, value) => value && setCategoryFilter(value)}
          size="small"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="Broadway">Broadway</ToggleButton>
          <ToggleButton value="Off-Broadway">
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Off-Broadway</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Off-Bway</Box>
          </ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          value={locationFilter}
          exclusive
          onChange={(_, value) => value !== null && setLocationFilter(value)}
          size="small"
        >
          <ToggleButton value="">All</ToggleButton>
          <ToggleButton value="Times Square">
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Times Square</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>TS</Box>
          </ToggleButton>
          <ToggleButton value="Lincoln Center">
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Lincoln Center</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>LC</Box>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Live Deals - Today's Shows (deals_populate and booths_open phases) */}
      {(displayPhase === 'deals_populate' || displayPhase === 'booths_open') && todayDeals.length > 0 && (
        <Card sx={{ mb: 3, border: '2px solid', borderColor: 'success.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6">
                Today's Live Deals ({todayDeals.length} shows)
              </Typography>
              {displayPhase === 'deals_populate' && (
                <Chip
                  label="Deals are live!"
                  color="success"
                  size="small"
                />
              )}
            </Box>
            {displayPhase === 'deals_populate' && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                TKTS booth hasn't officially opened yet, but deals are already available on the website.
              </Typography>
            )}
            {isMobile ? (
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                {todayDeals.map((deal, idx) => {
                  const formatted = formatVariations(deal.variations);
                  const prevDeal = idx > 0 ? todayDeals[idx - 1] : null;
                  const isCategoryBoundary = prevDeal && prevDeal.category === 'Broadway' && deal.category === 'Off-Broadway';
                  const dealDate = getDealDate(deal.all_showtimes);
                  return (
                    <Box
                      key={`today-${deal.show_name}`}
                      onClick={(e) => openShow(deal.show_name, e)}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        borderTop: isCategoryBoundary ? '3px solid' : '1px solid',
                        borderTopColor: isCategoryBoundary ? 'divider' : 'rgba(255,255,255,0.08)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.25 }}>
                        <Typography fontWeight="medium" variant="body2">{deal.show_name}</Typography>
                        <LateAddFlag lateAdditions={deal.late_additions} />
                        <ShowStatusBadgeChip badge={getShowStatusBadge(deal.show_name, statusFlags, dealDate)} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">{deal.category}</Typography>
                        {rarityByShow.has(deal.show_name) && <RarityStars size="sm" {...rarityByShow.get(deal.show_name)!} />}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <Box sx={{ minWidth: 60 }}>
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Time</Typography>
                          {formatted.showtimesDisplay}
                        </Box>
                        <Box sx={{ minWidth: 44 }}>
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Disc.</Typography>
                          {formatted.discountDisplay}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Price</Typography>
                          {formatted.priceDisplay}
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>At</Typography>
                          {formatted.locationsDisplay}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Show</TableCell>
                      <TableCell>Show Time</TableCell>
                      <TableCell>Discount</TableCell>
                      <TableCell>Price Range</TableCell>
                      <TableCell>Available At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todayDeals.map((deal, idx) => {
                      const formatted = formatVariations(deal.variations);
                      const prevDeal = idx > 0 ? todayDeals[idx - 1] : null;
                      const isCategoryBoundary = prevDeal && prevDeal.category === 'Broadway' && deal.category === 'Off-Broadway';
                      const dealDate = getDealDate(deal.all_showtimes);
                      return (
                        <TableRow
                          key={`today-${deal.show_name}`}
                          hover
                          sx={{
                            cursor: 'pointer',
                            verticalAlign: 'top',
                            ...(isCategoryBoundary && { borderTop: '3px solid', borderTopColor: 'divider' }),
                          }}
                          onClick={(e) => openShow(deal.show_name, e)}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                              <Typography fontWeight="medium">{deal.show_name}</Typography>
                              <LateAddFlag lateAdditions={deal.late_additions} />
                              <ShowStatusBadgeChip badge={getShowStatusBadge(deal.show_name, statusFlags, dealDate)} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">{deal.category}</Typography>
                              {rarityByShow.has(deal.show_name) && <RarityStars size="sm" {...rarityByShow.get(deal.show_name)!} />}
                            </Box>
                          </TableCell>
                          <TableCell>{formatted.showtimesDisplay}</TableCell>
                          <TableCell>{formatted.discountDisplay}</TableCell>
                          <TableCell>{formatted.priceDisplay}</TableCell>
                          <TableCell>{formatted.locationsDisplay}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tomorrow's Shows (deals_populate/booths_open) / Potential Deals (booths_closed/pre_opening) */}
      {/* In closed/pre_opening after midnight, show allTodayDeals as "Potential Deals for Today" */}
      {/* In closed/pre_opening before midnight, show tomorrowDeals as "Potential Deals for Tomorrow" */}
      {/* In deals_populate/booths_open, show tomorrowDeals as "Tomorrow's Deals" preview */}
      {((isClosedPhase && potentialDeals.length > 0) || (!isClosedPhase && tomorrowDeals.length > 0)) && (
        <Card sx={{ mb: 3, border: '2px solid', borderColor: isClosedPhase ? 'primary.main' : 'warning.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6">
                {isClosedPhase
                  ? `Potential Deals for ${pastMidnight ? 'Today' : 'Tomorrow'} (${potentialDeals.length} shows)`
                  : `Tomorrow's Deals (${tomorrowDeals.length} shows)`
                }
              </Typography>
              <Chip
                label={isClosedPhase ? 'Based on last scan' : 'Preview'}
                color={isClosedPhase ? 'primary' : 'warning'}
                size="small"
              />
            </Box>
            {isClosedPhase && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {pastMidnight
                  ? 'These shows were available at the booths yesterday and may be available when booths open today.'
                  : 'These shows were available at the booths today and may be available when booths reopen tomorrow.'
                }
              </Typography>
            )}
            {isMobile ? (
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                {(isClosedPhase ? potentialDeals : tomorrowDeals).map((deal, idx) => {
                  const dealsToRender = isClosedPhase ? potentialDeals : tomorrowDeals;
                  const filteredVariations = isClosedPhase ? deal.variations : filterTomorrowShowtimes(deal.variations);
                  const formatted = formatVariations(filteredVariations);
                  const prevDeal = idx > 0 ? dealsToRender[idx - 1] : null;
                  const isCategoryBoundary = prevDeal && prevDeal.category === 'Broadway' && deal.category === 'Off-Broadway';
                  const dealDate = getDealDate(deal.all_showtimes);
                  return (
                    <Box
                      key={`potential-${deal.show_name}`}
                      onClick={(e) => openShow(deal.show_name, e)}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        backgroundColor: isClosedPhase ? 'rgba(255,255,255,0.03)' : 'rgba(255,167,38,0.08)',
                        borderTop: isCategoryBoundary ? '3px solid' : '1px solid',
                        borderTopColor: isCategoryBoundary ? 'divider' : 'rgba(255,255,255,0.08)',
                        '&:hover': {
                          backgroundColor: isClosedPhase ? 'rgba(255,255,255,0.08)' : 'rgba(255,167,38,0.15)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.25 }}>
                        <Typography fontWeight="medium" variant="body2">{deal.show_name}</Typography>
                        <LateAddFlag lateAdditions={deal.late_additions} />
                        <ShowStatusBadgeChip badge={getShowStatusBadge(deal.show_name, statusFlags, dealDate)} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">{deal.category}</Typography>
                        {rarityByShow.has(deal.show_name) && <RarityStars size="sm" {...rarityByShow.get(deal.show_name)!} />}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <Box sx={{ minWidth: 60 }}>
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Time</Typography>
                          {formatted.showtimesDisplay}
                        </Box>
                        <Box sx={{ minWidth: 44 }}>
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Disc.</Typography>
                          {formatted.discountDisplay}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Price</Typography>
                          {formatted.priceDisplay}
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>At</Typography>
                          {formatted.locationsDisplay}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: isClosedPhase ? 'rgba(192, 90, 66, 0.12)' : 'rgba(200, 144, 48, 0.15)' }}>
                      <TableCell>Show</TableCell>
                      <TableCell>Show Time</TableCell>
                      <TableCell>Discount</TableCell>
                      <TableCell>Price Range</TableCell>
                      <TableCell>Available At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(isClosedPhase ? potentialDeals : tomorrowDeals).map((deal, idx) => {
                      const dealsToRender = isClosedPhase ? potentialDeals : tomorrowDeals;
                      const filteredVariations = isClosedPhase ? deal.variations : filterTomorrowShowtimes(deal.variations);
                      const formatted = formatVariations(filteredVariations);
                      const prevDeal = idx > 0 ? dealsToRender[idx - 1] : null;
                      const isCategoryBoundary = prevDeal && prevDeal.category === 'Broadway' && deal.category === 'Off-Broadway';
                      const dealDate = getDealDate(deal.all_showtimes);
                      return (
                        <TableRow
                          key={`potential-${deal.show_name}`}
                          hover
                          sx={{
                            cursor: 'pointer',
                            verticalAlign: 'top',
                            backgroundColor: isClosedPhase ? 'rgba(255, 255, 255, 0.03)' : 'rgba(200, 144, 48, 0.08)',
                            '&:hover': {
                              backgroundColor: isClosedPhase ? 'rgba(255, 255, 255, 0.08)' : 'rgba(200, 144, 48, 0.15)',
                            },
                            ...(isCategoryBoundary && { borderTop: '3px solid', borderTopColor: 'divider' }),
                          }}
                          onClick={(e) => openShow(deal.show_name, e)}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                              <Typography fontWeight="medium">{deal.show_name}</Typography>
                              <LateAddFlag lateAdditions={deal.late_additions} />
                              <ShowStatusBadgeChip badge={getShowStatusBadge(deal.show_name, statusFlags, dealDate)} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">{deal.category}</Typography>
                              {rarityByShow.has(deal.show_name) && <RarityStars size="sm" {...rarityByShow.get(deal.show_name)!} />}
                            </Box>
                          </TableCell>
                          <TableCell>{formatted.showtimesDisplay}</TableCell>
                          <TableCell>{formatted.discountDisplay}</TableCell>
                          <TableCell>{formatted.priceDisplay}</TableCell>
                          <TableCell>{formatted.locationsDisplay}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upcoming Shows - when booths closed and no tomorrow deals (after midnight case) */}
      {showUpcomingSection && (
        <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6">
                Upcoming Shows ({futureDeals.length} shows)
              </Typography>
              <Chip
                label="May be available when booths open"
                color="primary"
                size="small"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              TKTS booths are currently closed. These shows were available at the last scan and may be available again when booths reopen.
            </Typography>
            {isMobile ? (
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                {futureDeals.map((deal, idx) => {
                  const formatted = formatVariations(deal.variations);
                  const prevDeal = idx > 0 ? futureDeals[idx - 1] : null;
                  const isCategoryBoundary = prevDeal && prevDeal.category === 'Broadway' && deal.category === 'Off-Broadway';
                  const dealDate = getDealDate(deal.all_showtimes);
                  return (
                    <Box
                      key={`upcoming-${deal.show_name}`}
                      onClick={(e) => openShow(deal.show_name, e)}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        borderTop: isCategoryBoundary ? '3px solid' : '1px solid',
                        borderTopColor: isCategoryBoundary ? 'divider' : 'rgba(255,255,255,0.08)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.25 }}>
                        <Typography fontWeight="medium" variant="body2">{deal.show_name}</Typography>
                        <LateAddFlag lateAdditions={deal.late_additions} />
                        <ShowStatusBadgeChip badge={getShowStatusBadge(deal.show_name, statusFlags, dealDate)} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">{deal.category}</Typography>
                        {rarityByShow.has(deal.show_name) && <RarityStars size="sm" {...rarityByShow.get(deal.show_name)!} />}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <Box sx={{ minWidth: 60 }}>
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Time</Typography>
                          {formatted.showtimesDisplay}
                        </Box>
                        <Box sx={{ minWidth: 44 }}>
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Disc.</Typography>
                          {formatted.discountDisplay}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Price</Typography>
                          {formatted.priceDisplay}
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>At</Typography>
                          {formatted.locationsDisplay}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'rgba(192, 90, 66, 0.12)' }}>
                      <TableCell>Show</TableCell>
                      <TableCell>Show Time</TableCell>
                      <TableCell>Discount</TableCell>
                      <TableCell>Price Range</TableCell>
                      <TableCell>Available At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {futureDeals.map((deal, idx) => {
                      const formatted = formatVariations(deal.variations);
                      const prevDeal = idx > 0 ? futureDeals[idx - 1] : null;
                      const isCategoryBoundary = prevDeal && prevDeal.category === 'Broadway' && deal.category === 'Off-Broadway';
                      const dealDate = getDealDate(deal.all_showtimes);
                      return (
                        <TableRow
                          key={`upcoming-${deal.show_name}`}
                          hover
                          sx={{
                            cursor: 'pointer',
                            verticalAlign: 'top',
                            ...(isCategoryBoundary && { borderTop: '3px solid', borderTopColor: 'divider' }),
                          }}
                          onClick={(e) => openShow(deal.show_name, e)}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                              <Typography fontWeight="medium">{deal.show_name}</Typography>
                              <LateAddFlag lateAdditions={deal.late_additions} />
                              <ShowStatusBadgeChip badge={getShowStatusBadge(deal.show_name, statusFlags, dealDate)} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">{deal.category}</Typography>
                              {rarityByShow.has(deal.show_name) && <RarityStars size="sm" {...rarityByShow.get(deal.show_name)!} />}
                            </Box>
                          </TableCell>
                          <TableCell>{formatted.showtimesDisplay}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                              {formatted.discountDisplay}
                            </Box>
                          </TableCell>
                          <TableCell>{formatted.priceDisplay}</TableCell>
                          <TableCell>{formatted.locationsDisplay}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Hot Deals / High Demand - always directly above Expired Deals */}
      {hotDealsCard}

      {/* Not on TKTS Today - listed yesterday for today's showtimes but never appeared in any today scan */}
      {(displayPhase === 'booths_open' || displayPhase === 'deals_populate') && noShowDeals.length > 0 && (() => {
        const grouped = groupUnavailableDeals(noShowDeals);

        return (
          <Card sx={{ mb: 3, border: '1px solid', borderColor: 'warning.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6">
                  Not on TKTS Today ({grouped.length} shows)
                </Typography>
                <Chip label="Listed yesterday" color="warning" size="small" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These shows were listed on TKTS yesterday for today's performances but have not appeared in any of today's scans.
              </Typography>
              {isMobile ? (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  {grouped.map((deal, idx) => {
                    const prevDeal = idx > 0 ? grouped[idx - 1] : null;
                    const isCategoryBoundary = prevDeal && prevDeal.category === 'Broadway' && deal.category === 'Off-Broadway';
                    const dealDate = getDealDate(deal.showtimes);
                    return (
                      <Box
                        key={`noshow-${deal.show_name}`}
                        onClick={(e) => openShow(deal.show_name, e)}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          borderTop: isCategoryBoundary ? '3px solid' : '1px solid',
                          borderTopColor: isCategoryBoundary ? 'divider' : 'rgba(255,255,255,0.08)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.25 }}>
                          <Typography fontWeight="medium" variant="body2" color="text.secondary">{deal.show_name}</Typography>
                          <ShowStatusBadgeChip badge={getShowStatusBadge(deal.show_name, statusFlags, dealDate)} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">{deal.category}</Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mt: 0.5 }}>
                          <Box sx={{ minWidth: 60 }}>
                            <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Time</Typography>
                            {deal.showtimes.map((st, i) => (
                              <Typography key={i} variant="caption" display="block">{format(new Date(st), 'h:mm a')}</Typography>
                            ))}
                          </Box>
                          <Box sx={{ minWidth: 44 }}>
                            <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Disc.</Typography>
                            <Chip label={`${deal.discount}%`} color="default" size="small" sx={{ opacity: 0.6 }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Price</Typography>
                            <Typography variant="caption" color="text.secondary">{deal.priceRange}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>At</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {deal.locations.map((loc) => (
                                <Chip key={loc} label={loc === 'Times Square' ? 'TS' : 'LC'} size="small" variant="outlined"
                                  color={loc === 'Times Square' ? 'primary' : 'secondary'} />
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Paper>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'rgba(200, 144, 48, 0.12)' }}>
                        <TableCell>Show</TableCell>
                        <TableCell>Expected Time</TableCell>
                        <TableCell>Discount</TableCell>
                        <TableCell>Price Range</TableCell>
                        <TableCell>Was Listed At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grouped.map((deal, idx) => {
                        const prevDeal = idx > 0 ? grouped[idx - 1] : null;
                        const isCategoryBoundary = prevDeal && prevDeal.category === 'Broadway' && deal.category === 'Off-Broadway';
                        const dealDate = getDealDate(deal.showtimes);
                        return (
                          <TableRow
                            key={`noshow-${deal.show_name}`}
                            hover
                            sx={{
                              cursor: 'pointer',
                              verticalAlign: 'top',
                              ...(isCategoryBoundary && { borderTop: '3px solid', borderTopColor: 'divider' }),
                            }}
                            onClick={(e) => openShow(deal.show_name, e)}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Typography fontWeight="medium" color="text.secondary">{deal.show_name}</Typography>
                                <ShowStatusBadgeChip badge={getShowStatusBadge(deal.show_name, statusFlags, dealDate)} />
                              </Box>
                              <Typography variant="caption" color="text.secondary">{deal.category}</Typography>
                            </TableCell>
                            <TableCell>
                              {deal.showtimes.map((st, i) => (
                                <Box key={i} sx={{ mb: i < deal.showtimes.length - 1 ? 0.5 : 0 }}>
                                  {format(new Date(st), 'h:mm a')}
                                </Box>
                              ))}
                            </TableCell>
                            <TableCell>
                              <Chip label={`${deal.discount}%`} color="default" size="small" sx={{ opacity: 0.6 }} />
                            </TableCell>
                            <TableCell><Typography color="text.secondary">{deal.priceRange}</Typography></TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {deal.locations.map((loc) => (
                                  <Chip key={loc} label={loc === 'Times Square' ? 'TS' : 'LC'} size="small" variant="outlined"
                                    color={loc === 'Times Square' ? 'primary' : 'secondary'} />
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Expired Deals - showtime passed or within 1 hour, plus any deals only at a booth that closed mid-day (not shown in deals_populate) */}
      {displayPhase !== 'deals_populate' && (todayOnlyExpiredDeals.length > 0 || (displayPhase === 'booths_open' && boothClosedDeals.length > 0)) && (() => {
        const groupedExpired = groupUnavailableDeals(todayOnlyExpiredDeals);
        // Merge in booth-closed deals, skipping any show already present in groupedExpired
        const expiredNames = new Set(groupedExpired.map((d) => d.show_name));
        const boothClosedGrouped = convertBoothClosedToGrouped(boothClosedDeals).filter((d) => !expiredNames.has(d.show_name));
        const combined = [...boothClosedGrouped, ...groupedExpired].sort((a, b) => {
          const catOrder = (cat: string) => (cat === 'Broadway' ? 0 : 1);
          const catCompare = catOrder(a.category) - catOrder(b.category);
          if (catCompare !== 0) return catCompare;
          return a.show_name.localeCompare(b.show_name);
        });

        // Helper to format last seen time display
        const formatLastSeenExpired = (lastSeenByLocation: Record<string, string>, locations: string[]) => {
          const times = locations.map((loc) => lastSeenByLocation[loc]).filter(Boolean);
          const uniqueTimes = Array.from(new Set(times));
          if (uniqueTimes.length === 0) return '-';
          if (uniqueTimes.length === 1) return uniqueTimes[0];
          // Different times - show with location labels
          return locations
            .filter((loc) => lastSeenByLocation[loc])
            .map((loc) => `${lastSeenByLocation[loc]} (${loc === 'Times Square' ? 'TS' : 'LC'})`)
            .join(', ');
        };

        return (
          <Card sx={{ mb: 3, opacity: 0.7 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6">
                  {isClosedPhase
                    ? `${pastMidnight ? "Yesterday's" : "Today's"} Deals (${combined.length} shows)`
                    : `Expired Deals (${combined.length} shows)`
                  }
                </Typography>
                <Chip
                  label={isClosedPhase ? 'Completed' : 'No longer selling'}
                  color="default"
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isClosedPhase
                  ? `Deals that were available ${pastMidnight ? 'yesterday' : 'today'} (showtimes have passed)`
                  : 'These showtimes have passed or are starting soon (within 1 hour)'
                }
              </Typography>
              {isMobile ? (
                <Paper variant="outlined" sx={{ overflow: 'hidden', opacity: 0.7 }}>
                  {combined.map((deal, idx) => {
                    const prevDeal = idx > 0 ? combined[idx - 1] : null;
                    const isCategoryBoundary = prevDeal && prevDeal.category === 'Broadway' && deal.category === 'Off-Broadway';
                    const dealDate = getDealDate(deal.showtimes);
                    return (
                      <Box
                        key={`expired-${deal.show_name}`}
                        onClick={(e) => openShow(deal.show_name, e)}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          borderTop: isCategoryBoundary ? '3px solid' : '1px solid',
                          borderTopColor: isCategoryBoundary ? 'divider' : 'rgba(255,255,255,0.08)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.25 }}>
                          <Typography fontWeight="medium" variant="body2">{deal.show_name}</Typography>
                          <ShowStatusBadgeChip badge={getShowStatusBadge(deal.show_name, statusFlags, dealDate)} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">{deal.category}</Typography>
                          {rarityByShow.has(deal.show_name) && <RarityStars size="sm" {...rarityByShow.get(deal.show_name)!} />}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                          <Box sx={{ minWidth: 60 }}>
                            <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Time</Typography>
                            {deal.showtimes.map((st, i) => <Box key={i}>{format(new Date(st), 'h:mm a')}</Box>)}
                          </Box>
                          <Box sx={{ minWidth: 44 }}>
                            <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Disc.</Typography>
                            <Chip label={`${deal.discount}%`} color={deal.discount >= 50 ? 'success' : 'warning'} size="small" />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>Price</Typography>
                            <Typography variant="body2">{deal.priceRange}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              Seen: {formatLastSeenExpired(deal.lastSeenByLocation, deal.locations)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.25 }}>At</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {deal.locations.map((loc) => (
                                <Chip key={loc} label={loc === 'Times Square' ? 'TS' : 'LC'} size="small" variant="outlined"
                                  color={loc === 'Times Square' ? 'primary' : 'secondary'} />
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Paper>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Show</TableCell>
                        <TableCell>Show Time</TableCell>
                        <TableCell>Discount</TableCell>
                        <TableCell>Price Range</TableCell>
                        <TableCell>Last Seen</TableCell>
                        <TableCell>Location</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {combined.map((deal, idx) => {
                        const prevDeal = idx > 0 ? combined[idx - 1] : null;
                        const isCategoryBoundary = prevDeal && prevDeal.category === 'Broadway' && deal.category === 'Off-Broadway';
                        const dealDate = getDealDate(deal.showtimes);
                        return (
                          <TableRow
                            key={`expired-${deal.show_name}`}
                            hover
                            sx={{
                              cursor: 'pointer',
                              verticalAlign: 'top',
                              opacity: 0.7,
                              ...(isCategoryBoundary && { borderTop: '3px solid', borderTopColor: 'divider' }),
                            }}
                            onClick={(e) => openShow(deal.show_name, e)}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Typography fontWeight="medium">{deal.show_name}</Typography>
                                <ShowStatusBadgeChip badge={getShowStatusBadge(deal.show_name, statusFlags, dealDate)} />
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">{deal.category}</Typography>
                                {rarityByShow.has(deal.show_name) && <RarityStars size="sm" {...rarityByShow.get(deal.show_name)!} />}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {deal.showtimes.map((st, stIdx) => (
                                <Box key={stIdx} sx={{ mb: stIdx < deal.showtimes.length - 1 ? 0.5 : 0 }}>
                                  {format(new Date(st), 'h:mm a')}
                                </Box>
                              ))}
                            </TableCell>
                            <TableCell>
                              <Chip label={`${deal.discount}%`} color={deal.discount >= 50 ? 'success' : 'warning'} size="small" />
                            </TableCell>
                            <TableCell>{deal.priceRange}</TableCell>
                            <TableCell>{formatLastSeenExpired(deal.lastSeenByLocation, deal.locations)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {deal.locations.map((loc) => (
                                  <Chip key={loc} label={loc === 'Times Square' ? 'TS' : 'LC'} size="small" variant="outlined"
                                    color={loc === 'Times Square' ? 'primary' : 'secondary'} />
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Show message when booths are open but no today's active or expired deals exist */}
      {displayPhase === 'booths_open' && todayDeals.length === 0 && todayOnlyExpiredDeals.length === 0 && boothClosedDeals.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {currentDeals.length === 0
            ? 'Deals are not yet live — check back soon.'
            : 'No deals available with current filters.'}
        </Alert>
      )}
    </Box>
  );
};

export default Dashboard;
